import { useParams, useNavigate } from 'react-router-dom';
import Spinner from 'src/components/_common/Spinner/Spinner';
import { useIntl, FormattedMessage } from 'react-intl';
import { useEffect, useState } from 'react';
import ClassesContext from 'src/hooks/useClasses';
import LoggedInUserContext from 'src/hooks/useLoggedInUser';
import classnames from 'classnames';
import { RoleTypes } from 'src/models/roles';
import AddUsersToClassModal from 'src/pages/Teacher/TeacherClass/AddUsersToClassModal/AddUsersToClassModal';
import ClassFormsTable from 'src/components/ClassFormsTable/ClassFormsTable';
import Button from 'src/components/_common/Button/Button';
import ClassDates from 'src/components/ClassDates/ClassDates';
import { sortSimpleUserByDisplayNameComparer } from 'src/utils/userUtils';
import PageHeader from 'src/components/_common/PageHeader/PageHeader';
import Badge from 'src/components/_common/Badge/Badge';
import List from 'src/components/_common/List/List';
import ListItem from 'src/components/_common/List/ListItem';
import SigningFormStatus from 'src/components/SigningFormStatus/SigningFormStatus';
import StudentPassWarningIcon from 'src/components/StudentPassWarningIcon/StudentPassWarningIcon';
import { AdvisorDetails, SimpleUser } from 'src/models/classes';
import SupervisorModal from './SupervisorModal/SupervisorModal';
import CheckListModal from './CheckListModal/CheckListModal';

const TeacherClass = () => {
  const {
    getCurrentClass,
    currentClass,
    currentClassLoaded,
    usersForClass,
    getUsersForClass,
    addSupervisorToClass,
    getMyStudents,
    getStudentsResponsible,
    getMyAdvisors,
    myAdvisors,
    myStudents,
    myStudentsLoaded,
    studentsResponsible,
    studentsResponsibleLoaded,
    joinClassAsTeacher,
    leaveClassAsTeacher,
    isSaving,
  } = ClassesContext.useContainer();

  const { user, getUser } = LoggedInUserContext.useContainer();

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [supervisorModalOpen, setSupervisorModalOpen] = useState<boolean>(false);
  const [checkListModalOpen, setCheckListModalOpen] = useState<boolean>(true);
  const [hasStudentsResponsible, setHasStudentsResponsible] = useState<boolean>(false);

  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (classId) {
      getCurrentClass(+classId);
      getUsersForClass(+classId);
      getMyStudents(+classId);
      getStudentsResponsible(+classId);
      getMyAdvisors(+classId);
    }
  }, [classId]);

  useEffect(() => {
    if (currentClass) {
      setCheckListModalOpen(localStorage.getItem(`checklist-done-period-${currentClass.periodId}-class-${currentClass.id}`) !== 'true');
    }
  }, [currentClass]);

  useEffect(() => {
    if (studentsResponsible && studentsResponsibleLoaded) {
      setHasStudentsResponsible(studentsResponsible.length > 0);
    }
  }, [studentsResponsible, studentsResponsibleLoaded]);

  const userIsTeacherForClass = !!user?.teacherClasses?.includes(+classId!);

  const handleClick = () => {
    if (userIsTeacherForClass) {
      leaveClassAsTeacher(+classId!, getUser);
    } else {
      joinClassAsTeacher(+classId!, getUser);
    }
  };

  const handleOpenModal = (_roleType: RoleTypes) => {
    if (_roleType === RoleTypes.Advisor) {
      setSupervisorModalOpen(true);
    } else {
      setModalOpen(true);
    }
  };

  const handleCheckListClose = () => {
      localStorage.setItem(`checklist-done-period-${currentClass?.periodId}-class-${currentClass?.id}`, 'true');
      setCheckListModalOpen(false);
  };

  const navigateToStudent = (student: SimpleUser) => navigate(`/teacher/class/${classId}/student/${student.id}`);
  const navigateToSupervisor = (supervisor: SimpleUser) => navigate(`/teacher/class/${classId}/advisor/${supervisor.id}`);

  const addSupervisor = (_classId: number, advisorDetails: AdvisorDetails) => addSupervisorToClass(_classId, advisorDetails).then(supervisor => {
    if (supervisor) navigateToSupervisor(supervisor);
  });

  const supervisors = userIsTeacherForClass ? myAdvisors : usersForClass?.advisors;

  if (!currentClassLoaded && !myStudentsLoaded && !studentsResponsibleLoaded && user?.id) return <Spinner />;

  return (
    <>
      <PageHeader title={currentClass?.subject?.subjectCode} subTitle={currentClass?.subject?.name}>
        <Badge>{currentClass?.period?.name}</Badge>
      </PageHeader>
      <ClassDates />
      <div className="text-center mt-8">
        <Button
          onClick={() => handleClick()}
          className={classnames({ 'bg-danger': userIsTeacherForClass }, 'self-center')}
          isSaving={isSaving}
        ><FormattedMessage id={userIsTeacherForClass ? 'leave-class' : 'join-class'} />
        </Button>
        <Button
          onClick={() => setCheckListModalOpen(true)}
          className="ml-8"
          isSaving={isSaving}
        ><FormattedMessage id="check-list-show" />
        </Button>
      </div>
      <ClassFormsTable baseRoute="/teacher" currentClass={currentClass} subjectCode={currentClass?.subject?.subjectCode} />
      {userIsTeacherForClass
        && (
        <>
          <List
            className="pt-8"
            title={formatMessage({ id: 'my-students' })}
            emptyListMessage={formatMessage({ id: 'no-students-assigned' }, { subjectCode: currentClass?.subject?.subjectCode })}
            listIsEmpty={!myStudents?.length}
            titleButtonAriaLabel={formatMessage({ id: 'addStudent' })}
            onTitleClick={() => handleOpenModal(RoleTypes.Student)}
          >
            {myStudents?.sort(sortSimpleUserByDisplayNameComparer).map(student => (
              <ListItem key={student.id} onClick={() => navigateToStudent(student)}>
                <span className="inline-flex items-center flex-wrap">
                  {student.displayName}
                  <StudentPassWarningIcon warning={student.studentPassWarning ?? null} />
                  <SigningFormStatus signingForm={student.signingForm ?? null} />
                </span>
              </ListItem>
            ))}
          </List>
          {hasStudentsResponsible && (
            <List
              className="pt-8"
              title={formatMessage({ id: 'students-responsible' })}
              listIsEmpty={!studentsResponsible?.length}
              canCollapse={true}
            >
              {studentsResponsible!.sort(sortSimpleUserByDisplayNameComparer).map(student => (
                <ListItem key={student.id} onClick={() => navigateToStudent(student)}>
                  <span className="inline-flex items-center flex-wrap">
                    {student.displayName}
                    <StudentPassWarningIcon warning={student.studentPassWarning ?? null} />
                    <SigningFormStatus signingForm={student.signingForm ?? null} />
                  </span>
                </ListItem>
              ))}
            </List>
          )}
        </>
        )}
      <List
        className="pt-8"
        title={formatMessage({ id: 'supervisors' })}
        titleButtonAriaLabel={formatMessage({ id: 'addSupervisor' })}
        onTitleClick={() => handleOpenModal(RoleTypes.Advisor)}
        emptyListMessage={userIsTeacherForClass
          ? formatMessage({ id: 'no-supervisors-for-my-students' })
          : formatMessage({ id: 'table-none-added' }, { name: formatMessage({ id: 'supervisors' }).toLowerCase() })}
        listIsEmpty={!supervisors?.length}
        canCollapse={true}
      >
        {supervisors?.sort(sortSimpleUserByDisplayNameComparer).map(supervisor => (
          <ListItem key={supervisor.id} onClick={() => navigateToSupervisor(supervisor)}>
            {supervisor.displayName}
          </ListItem>
        ))}
      </List>
      {modalOpen
        && (
          <AddUsersToClassModal
            isOpen={!!modalOpen}
            onClose={() => setModalOpen(false)}
            classId={+classId!}
          />
        )}
      {supervisorModalOpen && currentClass
        && (
          <SupervisorModal
            addSupervisor={addSupervisor}
            currentClass={currentClass}
            onClose={() => setSupervisorModalOpen(false)}
            supervisorModalOpen={supervisorModalOpen}
            isSaving={isSaving}
          />
        )}
      {checkListModalOpen && currentClass
        && (
          <CheckListModal
            checkListModalOpen={checkListModalOpen}
            onClose={handleCheckListClose}
          />
        )}
    </>
  );
};

export default TeacherClass;
